import type { VariantProps } from 'class-variance-authority';
import type React from 'react';

import { cn } from '../../lib/utils';
import { Button } from '../button';
import { Icon, type IconProps } from '../icon';
import { StatusDot } from '../status-dot';
import { alertVariants } from './variants';

const Alert = ({
  variant = 'active',
  size = 'md',
  className,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) => (
  <div
    data-slot={'alert'}
    role={'alert'}
    className={cn(alertVariants({ variant, size }), className)}
    {...props}
  />
);

const AlertDismiss = ({
  className,
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'variant' | 'style' | 'size'>) => (
  <Button
    data-slot={'alert-dismiss'}
    variant={'neutral'}
    style={'ghost'}
    size={'icon'}
    className={cn('size-auto p-0 ml-auto shrink-0', className)}
    aria-label={'Dismiss'}
    {...props}
  >
    <Icon name={'x'} size={'md'} />
  </Button>
);

const AlertInfo = ({ className, ...props }: Omit<IconProps, 'name'>) => (
  <Icon
    name={'info-circle'}
    size={'sm'}
    className={cn(className)}
    {...props}
  />
);

const AlertHeader = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    data-slot={'alert-header'}
    className={cn('flex gap-1 items-center', className)}
    {...props}
  />
);

const AlertTitle = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    data-slot={'alert-title'}
    className={cn('font-medium text-primary', className)}
    {...props}
  />
);

const AlertSubtitle = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    data-slot={'alert-subtitle'}
    className={cn('text-lg font-medium', className)}
    {...props}
  />
);

const AlertDescription = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div
    data-slot={'alert-description'}
    className={cn('text-sm font-medium', className)}
    {...props}
  />
);

Alert.Dismiss = AlertDismiss;
Alert.Description = AlertDescription;
Alert.Header = AlertHeader;
Alert.Info = AlertInfo;
Alert.Status = StatusDot;
Alert.Subtitle = AlertSubtitle;
Alert.Title = AlertTitle;

export { Alert };
